import { useState } from "react";
import type { PtzDirection } from "@lms/shared";

import { Icon } from "./Icon.js";
import { ApiError } from "../api/client.js";
import { useI18n } from "../i18n.js";
import {
  generateRingWaypoints,
  type RecordedWaypoint,
  type RingBand,
} from "../ptz-geometry.js";
import { useSession } from "../session.js";

/**
 * REGISTRAZIONE PERCORSO, portata da `PTZJoystick.qml`.
 *
 * Due modi di costruire una ronda, e non sono due gusti:
 *
 *  - **PUNTI**: si porta la testa dove serve e si segna. Ogni punto ha
 *    un'attesa, perche' il sensore e' a domanda e risposta e un bersaglio si
 *    misura fermi. E' il modo giusto per un impianto con pochi obiettivi
 *    precisi.
 *  - **GIRO 360° a fasce**: si segna solo un livello di tilt, e da ogni
 *    fascia escono dodici punti a giro. Nato perche' marcare a mano dodici
 *    posizioni per ognuno di tre livelli sono trentasei operazioni identiche,
 *    e chi le fa in campo ne sbaglia qualcuna.
 *
 * Il giro a fasce **richiede l'encoder grezzo del rig**: il tilt si cattura
 * dal conteggio, non dai gradi, che sul firmware Flexwatch hanno la scala
 * compressa. Su un rig che non lo pubblica il bottone e' spento e dice
 * perche' — come nell'app desktop, dove semplicemente non fa niente.
 *
 * Il mini-joystick c'e' perche' fra un punto e l'altro la testa va mossa, e le
 * frecce grandi stanno nell'altra scheda.
 */
export function TraceRecorder({
  clusterId,
  canControl,
  angle,
  tilt,
  zoom,
  encPan,
  encTilt,
  hold,
  onSaved,
}: {
  clusterId: string;
  canControl: boolean;
  angle: number;
  tilt: number;
  zoom: number;
  /** -1 quando il rig non pubblica l'encoder. */
  encPan: number;
  encTilt: number;
  /** Gli stessi gestori premi-e-tieni del joystick grande. */
  hold: (dir: PtzDirection) => Record<string, () => void>;
  onSaved: (traceId: string) => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();

  const [recording, setRecording] = useState(false);
  const [ringMode, setRingMode] = useState(false);
  const [waypoints, setWaypoints] = useState<RecordedWaypoint[]>([]);
  const [bands, setBands] = useState<RingBand[]>([]);
  const [showSave, setShowSave] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const hasEncoder = encPan >= 0 && encTilt >= 0;

  const label = (i: number) => LETTERS[i] ?? `P${i + 1}`;

  const addWaypoint = () => {
    setWaypoints((cur) => [
      ...cur,
      {
        label: label(cur.length),
        angle,
        tilt,
        zoom,
        dwellSeconds: 5,
        // L'encoder si mette solo se c'e': un waypoint con dentro un -1
        // manderebbe la ronda in spazio encoder alla posizione zero.
        ...(hasEncoder ? { encPan, encTilt } : {}),
      },
    ]);
  };

  const removeWaypoint = (idx: number) =>
    setWaypoints((cur) =>
      cur.filter((_, i) => i !== idx).map((w, i) => ({ ...w, label: label(i) })),
    );

  const setDwell = (idx: number, dwell: number) =>
    setWaypoints((cur) =>
      cur.map((w, i) => (i === idx ? { ...w, dwellSeconds: Math.max(0, dwell) } : w)),
    );

  const addBand = () => {
    if (!hasEncoder) return;
    setBands((cur) => [
      ...cur,
      { encTilt, tiltDeg: tilt, label: `T${cur.length + 1}` },
    ]);
  };

  const removeBand = (idx: number) =>
    setBands((cur) =>
      cur.filter((_, i) => i !== idx).map((b, i) => ({ ...b, label: `T${i + 1}` })),
    );

  const reset = () => {
    setRecording(false);
    setWaypoints([]);
    setBands([]);
    setShowSave(false);
    setName("");
  };

  const record = () => {
    if (!recording) {
      setRecording(true);
      setWaypoints([]);
      setBands([]);
    }
    if (ringMode) addBand();
    else addWaypoint();
  };

  const enough = ringMode ? bands.length >= 1 : waypoints.length >= 2;

  const save = async () => {
    const wps = ringMode ? generateRingWaypoints(bands, encPan, zoom) : waypoints;
    if (!name.trim() || !clusterId || wps.length < 2) return;
    setSaving(true);
    try {
      const trace = await api.call<{ id: string }>("ptz-traces.create", {
        clusterId,
        name: name.trim(),
        waypoints: wps,
      });
      // La traccia appena registrata diventa quella attiva, come nell'app
      // desktop: nessuno registra un percorso per poi doverlo selezionare.
      await api.call("clusters.update", { id: clusterId, activePtzTraceId: trace.id });
      reset();
      onSaved(trace.id);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ptz__group rec">
      <div className="ptz__title">{t("ptz.recordPath")}</div>
      {error && <p className="alert">{error}</p>}

      <div className="rec__modes">
        {[
          { ring: false, label: t("ptz.modePoints") },
          { ring: true, label: t("ptz.mode360") },
        ].map((m) => (
          <button
            key={String(m.ring)}
            className={`rec__mode${ringMode === m.ring ? " rec__mode--on" : ""}`}
            onClick={() => setRingMode(m.ring)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {ringMode && <p className="rec__hint">{t("ptz.ringHint")}</p>}

      <button
        className="rec__btn"
        disabled={!canControl || !clusterId || (ringMode && !hasEncoder)}
        // Detto invece che taciuto: nell'app desktop questo bottone su un rig
        // senza encoder semplicemente non fa niente, ed e' il tipo di cosa che
        // manda a cercare un guasto che non c'e'.
        title={ringMode && !hasEncoder ? t("ptz.ringNoEncoder") : undefined}
        onClick={record}
      >
        <i className="rec__dot" />
        {ringMode ? t("ptz.addBand", { p: bands.length + 1 }) : t("ptz.recordWaypoint")}
      </button>

      {/* Fra un punto e l'altro la testa va mossa, e le frecce grandi stanno
          nella scheda MANUALE. */}
      {recording && (
        <div className="rec__joy">
          <button className="rec__arrow" {...hold("up")}>
            <Icon name="arrow-up" size={16} />
          </button>
          <div className="rec__joyRow">
            <button className="rec__arrow" {...hold("left")}>
              <Icon name="arrow-left" size={16} />
            </button>
            <span className="rec__joyGap" />
            <button className="rec__arrow" {...hold("right")}>
              <Icon name="arrow-right" size={16} />
            </button>
          </div>
          <button className="rec__arrow" {...hold("down")}>
            <Icon name="arrow-down" size={16} />
          </button>
        </div>
      )}

      <p className="muted small">
        {ringMode
          ? t("ptz.bandsCount", { p: bands.length })
          : `${waypoints.length} ${t("ptz.pointsRecorded")}`}
      </p>

      {recording && !ringMode && (
        <ul className="rec__list">
          {waypoints.map((w, i) => (
            <li key={w.label}>
              <b>{w.label}</b>
              <span className="rec__pos">
                P{w.angle.toFixed(0)}° T{w.tilt.toFixed(0)}°
              </span>
              <button className="rec__step" onClick={() => setDwell(i, w.dwellSeconds - 1)}>
                −
              </button>
              <span className="rec__dwell">{w.dwellSeconds}s</span>
              <button className="rec__step" onClick={() => setDwell(i, w.dwellSeconds + 1)}>
                +
              </button>
              <button className="rec__del" onClick={() => removeWaypoint(i)}>
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {recording && ringMode && (
        <ul className="rec__list">
          {bands.map((b, i) => (
            <li key={b.label}>
              <b>{b.label}</b>
              <span className="rec__pos">{t("ptz.ringBand", { p: b.tiltDeg.toFixed(1) })}</span>
              <button className="rec__del" onClick={() => removeBand(i)}>
                <Icon name="x" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {recording && !showSave && enough && (
        <div className="ptz__row">
          <button onClick={reset}>{t("ptz.cancel")}</button>
          <button className="btn--primary" onClick={() => setShowSave(true)}>
            {t("ptz.save")}
          </button>
        </div>
      )}

      {showSave && (
        <div className="rec__save">
          <label className="field">
            <span className="field__label">{t("ptz.traceName")}</span>
            <input
              value={name}
              placeholder={t("ptz.traceNamePlaceholder")}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void save();
              }}
            />
          </label>
          <div className="ptz__row">
            <button onClick={() => setShowSave(false)}>{t("ptz.cancel")}</button>
            <button
              className="btn--primary"
              disabled={!name.trim() || saving}
              onClick={() => void save()}
            >
              {saving ? "…" : t("ptz.save")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
